import { useTranslation } from 'react-i18next';

import { FaceFrownIcon } from '@heroicons/react/24/outline';

import Button from '~/components/Button';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import PostCard from '~/components/PostCard';
import PostCardv3 from '~/components/PostCardv3';
import type { PostCardStyles } from '~/components/PostCardv3/types';
import type { Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';

import NewsFeedPostsLoader from './NewsFeedPostsLoader';

interface PostsProps {
  publication: Publication;
  posts: Post[];
  isSearching: boolean;
  isLoading: boolean;
  showLoadMore: boolean;
  fetchPosts: () => void;
  cardStyles: PostCardStyles;
}

const NewsFeedPosts = ({
  publication,
  posts,
  isSearching,
  isLoading,
  showLoadMore,
  fetchPosts,
  cardStyles,
}: PostsProps) => {
  const { isEnabled: hasWebThemesEnabled } = useWebThemeContext();
  const { t } = useTranslation();

  if (isSearching) {
    return <NewsFeedPostsLoader publication={publication} />;
  }

  if (posts.length === 0) {
    return (
      <div className="flex w-full items-center justify-center space-x-2 pb-8 opacity-40">
        <FaceFrownIcon className="h-8 w-8 text-wt-text-on-background" />
        <span className="py-8 text-xl font-medium text-wt-text-on-background">{t('search.posts.no_results')}</span>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 pb-8 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-8">
        {posts.map((post: Post, _index: number) => {
          if (hasWebThemesEnabled) {
            return <PostCardv3 key={post.id} post={post} cardStyles={cardStyles} />;
          }
          return (
            <PostCard
              key={post.id}
              publication={publication}
              post={post}
              titleSize="lg"
              descriptionSize="md"
              theme="default"
              showImage={true}
              variant="news"
            />
          );
        })}
      </div>
      {showLoadMore && (
        <div className="mt-6 flex w-full justify-center">
          <Button className="transition-all group-hover:brightness-110" onClick={fetchPosts} variant="secondary">
            {isLoading ? `${t('shared.actions.loading')}...` : t('shared.cta.load_more')}
          </Button>
        </div>
      )}
    </>
  );
};

export default NewsFeedPosts;
